import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  Typography,
  Alert,
  Box
} from '@mui/material';

const EncodingSelector = ({ open, file, onEncodingSelected, onCancel }) => {
  const [selectedEncoding, setSelectedEncoding] = useState('utf-8');
  const [preview, setPreview] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  const encodings = [
    { value: 'utf-8', label: 'UTF-8' },
    { value: 'windows-1250', label: 'Windows-1250 (Środkowa Europa)' },
    { value: 'iso-8859-2', label: 'ISO-8859-2 (Łaciński-2)' },
    { value: 'windows-1252', label: 'Windows-1252 (Zachodni)' },
    { value: 'utf-16', label: 'UTF-16' }
  ];

  const handleEncodingChange = async (encoding) => {
    setSelectedEncoding(encoding);
    setIsLoading(true);

    try {
      const reader = new FileReader();
      reader.onload = (event) => {
        const content = event.target.result;
        // Pokaż pierwsze 500 znaków jako podgląd
        const previewText = content.substring(0, 500);
        setPreview(previewText);
        setIsLoading(false);
      };
      reader.onerror = () => {
        setPreview('Błąd podczas odczytu pliku');
        setIsLoading(false);
      };
      reader.readAsText(file, encoding);
    } catch (error) {
      setPreview('Nie można odczytać pliku z tym kodowaniem');
      setIsLoading(false);
    }
  };

  const handleConfirm = async () => {
    setIsLoading(true);

    try {
      const reader = new FileReader();
      reader.onload = (event) => {
        const content = event.target.result;
        onEncodingSelected({
          file,
          content,
          name: file.name,
          size: file.size,
          encoding: selectedEncoding
        });
        setIsLoading(false);
      };
      reader.onerror = () => {
        onCancel();
        setIsLoading(false);
      };
      reader.readAsText(file, selectedEncoding);
    } catch (error) {
      onCancel();
      setIsLoading(false);
    }
  };

  // Auto-load preview for initial encoding
  React.useEffect(() => {
    if (open && file) {
      handleEncodingChange(selectedEncoding);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, file]);

  return (
    <Dialog open={open} onClose={onCancel} maxWidth="md" fullWidth>
      <DialogTitle>Wybierz kodowanie pliku</DialogTitle>
      <DialogContent>
        <Alert severity="info" sx={{ mb: 3 }}>
          Wykryto problemy z kodowaniem znaków. Wybierz właściwe kodowanie dla Twojego pliku MT940.
        </Alert>

        <FormControl fullWidth sx={{ mb: 3 }}>
          <InputLabel>Kodowanie</InputLabel>
          <Select
            value={selectedEncoding}
            label="Kodowanie"
            onChange={(e) => handleEncodingChange(e.target.value)}
            disabled={isLoading}
          >
            {encodings.map((encoding) => (
              <MenuItem key={encoding.value} value={encoding.value}>
                {encoding.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <Typography variant="subtitle2" gutterBottom>
          Podgląd zawartości pliku:
        </Typography>
        <Box
          sx={{
            p: 2,
            bgcolor: 'grey.100',
            borderRadius: 1,
            fontFamily: 'monospace',
            fontSize: '0.875rem',
            maxHeight: 300,
            overflow: 'auto',
            whiteSpace: 'pre-wrap',
            border: 1,
            borderColor: 'grey.300'
          }}
        >
          {isLoading ? 'Ładowanie podglądu...' : preview || 'Brak podglądu'}
        </Box>

        <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
          Sprawdź czy polskie znaki (ą, ć, ę, ł, ń, ó, ś, ź, ż) wyświetlają się poprawnie w podglądzie.
        </Typography>
      </DialogContent>
      <DialogActions>
        <Button onClick={onCancel} disabled={isLoading}>
          Anuluj
        </Button>
        <Button
          onClick={handleConfirm}
          variant="contained"
          disabled={isLoading || !preview}
        >
          Użyj tego kodowania
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default EncodingSelector;